import { z } from "zod";
import { zql } from "../schema";
import type { MutateSurface } from "../message-builders";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { ROLE_RANK, requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";
import type { MutatorCtx } from "./messages-cores";

/**
 * Schema for `workspace_members.delete`. Declared at module scope so
 * both the public mutator (in `workspaces.ts`) AND the server-mutator
 * extension override in `server/server-mutators.ts` share a single
 * source of truth.
 */
export const workspaceMemberDeleteSchema = z.object({ id: z.string() });

/**
 * Schema for `workspace_members.update`. Module-scoped for the same
 * reason as the delete schema — the public mutator and the
 * server-mutator override that fires the `role_upgraded` notification
 * both reference it.
 */
export const workspaceMemberUpdateSchema = z.object({
  id: z.string(),
  role: z.string().optional(),
});

/**
 * Shared core for `workspace_members.delete`. Performs the auth check
 * (server-only) + soft-delete write. Returns the workspace_id +
 * user_id of the tombstoned row + the ms-epoch we stamped on
 * `removed_at` so a server-mutator override can fire a Phase B
 * dual-write into the D1 control plane without re-reading.
 *
 * Returns `null` when:
 *   - the row doesn't exist (no-op short-circuit),
 *   - this is the client-side optimistic pass (no auth + cache lookup),
 *     in which case `tx.mutate.workspace_members.update` still runs but
 *     the server-mutator extension shouldn't dual-write (it'll get a
 *     real return value on the server-side replay).
 *
 * Server-side: returns `{ workspaceId, userId, removedAt }`.
 *
 * Lives in shared so the client mutator + the server-mutator
 * extension share a single source of truth — per
 * `.claude/rules/mutators.md` ("server mutators extend the client
 * mutator's behavior via shared core helpers").
 */
export interface WorkspaceMemberDeleteResult {
  workspaceId: string;
  userId: string;
  removedAt: number;
}

export async function workspaceMemberDeleteCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof workspaceMemberDeleteSchema>;
  ctx: MutatorCtx;
}): Promise<WorkspaceMemberDeleteResult | null> {
  let targetWorkspaceId: string | null = null;
  let targetUserId: string | null = null;

  if (tx.location === "server") {
    // Server-side calls always carry a verified JWT subject. Bail
    // explicitly so the type narrowing for `ctx.sub` below isn't a
    // bang-assertion — and so a missing sub is a clear server-side
    // contract violation rather than a downstream Zero error.
    if (!ctx?.sub) {
      throw new Error("Unauthorized: missing caller identity");
    }
    const callerSub = ctx.sub;
    const target = await tx.run(
      zql.workspace_members.where("id", args.id).one(),
    );
    if (!target) return null;
    targetWorkspaceId = target.workspace_id;
    targetUserId = target.user_id;

    if (target.user_id === callerSub) {
      // Self-remove — anyone except primary_owner
      if (target.role === "primary_owner") {
        throw new Error("Primary owner cannot leave workspace");
      }
    } else {
      // Removing others — must be admin+ AND strictly higher rank
      const actor = await requireWorkspaceRole(
        tx,
        makeWorkspaceId(target.workspace_id),
        makeUserId(callerSub),
      );
      const actorRank = ROLE_RANK[actor.role] ?? 0;
      const targetRank = ROLE_RANK[target.role] ?? 0;
      if (actorRank <= targetRank) {
        throw new Error("Cannot remove member with equal or higher role");
      }
    }
  }
  // Soft-delete: preserves the row so branch-2 userAccessFilter
  // (share-a-workspace visibility) keeps resolving for their historical
  // messages + reactions. The `on_workspace_member_removal` trigger
  // (Step 1b) cascades downstream cleanup.
  const removedAt = Date.now();
  await tx.mutate.workspace_members.update({
    id: args.id,
    removed_at: removedAt,
    removed_by: ctx?.sub,
  });

  if (tx.location !== "server" || !targetWorkspaceId || !targetUserId) {
    return null;
  }
  return {
    workspaceId: targetWorkspaceId,
    userId: targetUserId,
    removedAt,
  };
}

/**
 * Shared core for `workspace_members.update`. Runs the rank-aware
 * role-change auth (server-only) + the write, and reports back when a
 * server-side write actually RAISED the target's rank (collaborator→
 * member, member→admin, …) so the server-mutator override can post a
 * `role_upgraded` inbox notification.
 *
 * Returns `null` when:
 *   - this is the client-side optimistic pass (no auth + no cache role
 *     read; the write still runs but the override must not notify — the
 *     server replay returns the real value),
 *   - the target row doesn't exist (no-op short-circuit),
 *   - no `role` change was requested,
 *   - the change is a demotion or lateral move (rank not increased).
 *
 * Server-side upgrade: returns `{ workspaceId, userId, oldRole, newRole }`.
 *
 * Lives in shared so the client mutator + server-mutator extension share
 * one source of truth — per `.claude/rules/mutators.md`. The notification
 * itself is an outbox side effect and stays in the server extension.
 */
export interface WorkspaceMemberUpdateResult {
  workspaceId: string;
  userId: string;
  oldRole: string;
  newRole: string;
}

export async function workspaceMemberUpdateCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof workspaceMemberUpdateSchema>;
  ctx: MutatorCtx;
}): Promise<WorkspaceMemberUpdateResult | null> {
  let upgrade: WorkspaceMemberUpdateResult | null = null;

  if (tx.location === "server") {
    // Server-side calls always carry a verified JWT subject. Bail
    // explicitly so the `ctx.sub` reads below narrow without a
    // bang-assertion (mirrors workspaceMemberDeleteCore).
    if (!ctx?.sub) {
      throw new Error("Unauthorized: missing caller identity");
    }
    const callerSub = ctx.sub;

    const target = await tx.run(
      zql.workspace_members.where("id", args.id).one(),
    );
    if (!target) return null;

    // Cannot change own role
    if (target.user_id === callerSub) {
      throw new Error("Unauthorized: cannot change own role");
    }

    const actor = await tx.run(
      zql.workspace_members
        .where("workspace_id", target.workspace_id)
        .where("user_id", callerSub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!actor) {
      throw new Error("Unauthorized: not a workspace member");
    }

    const actorRank = ROLE_RANK[actor.role] ?? 0;
    const targetRank = ROLE_RANK[target.role] ?? 0;

    // Must be admin+ to change roles
    if (actorRank < (ROLE_RANK["admin"] ?? 0)) {
      throw new Error("Unauthorized: must be admin or higher");
    }

    if (args.role) {
      const newRank = ROLE_RANK[args.role] ?? 0;

      if (args.role === "primary_owner") {
        throw new Error("Cannot set primary_owner via mutation");
      }
      // Can't change role of equal or higher rank (unless primary_owner)
      if (actor.role !== "primary_owner" && targetRank >= actorRank) {
        throw new Error("Cannot modify member with equal or higher role");
      }
      // Can't promote above own rank (owners CAN promote to owner)
      if (actor.role !== "primary_owner" && newRank > actorRank) {
        throw new Error("Cannot promote above own role");
      }
      // Admins can only promote up to admin level
      if (actor.role === "admin" && newRank > (ROLE_RANK["admin"] ?? 0)) {
        throw new Error("Admins cannot promote above admin");
      }

      // Only a strict rank increase is an "upgrade" worth notifying.
      // Demotions / lateral moves leave `upgrade` null → no notification.
      if (newRank > targetRank) {
        upgrade = {
          workspaceId: target.workspace_id,
          userId: target.user_id,
          oldRole: target.role,
          newRole: args.role,
        };
      }
    }
  }

  await tx.mutate.workspace_members.update(args);
  return upgrade;
}
